/**
 * Where an agent's words come from. The built-in provider is Claude through the official
 * SDK — either via the editor's own proxy (`/api/agent`, the key stays on the server) or
 * straight to the API with the user's own key. A scripted stand-in drives the same loop
 * for UI checks and tests without a model.
 */
import Anthropic from '@anthropic-ai/sdk';
import type { Effort } from './agents';
import { t } from '../i18n/I18n';

export type StreamEvent = Anthropic.Beta.BetaRawMessageStreamEvent;
export type Message = Anthropic.Beta.BetaMessage;
export type MessageParam = Anthropic.Beta.BetaMessageParam;
export type TextBlockParam = Anthropic.Beta.BetaTextBlockParam;
export type ToolParam = Anthropic.Beta.BetaTool;
export type ContentBlockParam = Anthropic.Beta.BetaContentBlockParam;
export type ToolResultBlockParam = Anthropic.Beta.BetaToolResultBlockParam;

export interface ProviderRequest {
  model: string;
  system: TextBlockParam[];
  messages: MessageParam[];
  tools: ToolParam[];
  maxTokens: number;
  effort?: Effort;
  signal: AbortSignal;
}

export interface ProviderRun {
  events: AsyncIterable<StreamEvent>;
  final(): Promise<Message>;
}

export interface AgentProvider {
  readonly id: string;
  run(request: ProviderRequest): ProviderRun;
}

/** The proxy path the deployed editor serves; the SDK appends `/v1/messages`. */
export const PROXY_BASE_PATH = '/api/agent';

export interface ClaudeProviderOptions {
  /** The proxy origin (`https://host/api/agent`) or nothing for the API itself. */
  baseURL?: string;
  /** The user's own key; with the proxy a placeholder travels and the server swaps it. */
  apiKey?: string;
  fetch?: typeof fetch;
}

const supportsAdaptiveThinking = (model: string) => !/haiku/i.test(model);
const supportsFallbacks = (model: string) => /claude-(opus-5|fable|mythos)/i.test(model);

export class ClaudeProvider implements AgentProvider {
  readonly id = 'claude';
  constructor(private readonly options: ClaudeProviderOptions = {}) {}

  run(request: ProviderRequest): ProviderRun {
    const client = new Anthropic({
      apiKey: this.options.apiKey ?? 'proxy',
      baseURL: this.options.baseURL,
      dangerouslyAllowBrowser: true,
      maxRetries: 1,
      ...(this.options.fetch ? { fetch: this.options.fetch } : {}),
    });
    const stream = client.beta.messages.stream({
      model: request.model,
      max_tokens: request.maxTokens,
      system: request.system,
      messages: request.messages,
      tools: request.tools,
      ...(supportsAdaptiveThinking(request.model) ? { thinking: { type: 'adaptive', display: 'summarized' } } : {}),
      ...(request.effort ? { output_config: { effort: request.effort } } : {}),
      ...(supportsFallbacks(request.model) ? { betas: ['server-side-fallback-2026-07-01'], fallbacks: 'default' } : {}),
    }, { signal: request.signal });
    return { events: stream, final: () => stream.finalMessage() };
  }
}

/** A readable reason for the chat, from whatever the SDK threw. */
export function describeProviderError(error: unknown): { message: string; retryable: boolean; notConfigured?: boolean } {
  if (error instanceof Anthropic.APIError) {
    const body = error.error as { error?: { type?: string; message?: string } } | undefined;
    const type = body?.error?.type;
    if (type === 'agent_not_configured') return { message: t('#AgentErrorNotConfigured'), retryable: false, notConfigured: true };
    if (error instanceof Anthropic.AuthenticationError) return { message: t('#AgentErrorApiKey'), retryable: false };
    if (error instanceof Anthropic.PermissionDeniedError) return { message: t('#AgentErrorForbidden', body?.error?.message ?? error.message), retryable: false };
    if (error instanceof Anthropic.RateLimitError) return { message: t('#AgentErrorRateLimit'), retryable: true };
    if (error instanceof Anthropic.BadRequestError) return { message: t('#AgentErrorBadRequest', body?.error?.message ?? error.message), retryable: false };
    if ((error.status ?? 0) >= 500) return { message: t('#AgentErrorServiceDown'), retryable: true };
    return { message: body?.error?.message ?? error.message, retryable: false };
  }
  if (error instanceof Anthropic.APIConnectionError) return { message: t('#AgentErrorNoConnection'), retryable: true };
  if (error instanceof Error && error.name === 'AbortError') return { message: t('#AgentStopped'), retryable: false };
  return { message: error instanceof Error ? error.message : String(error), retryable: true };
}

// ---- the scripted stand-in --------------------------------------------------------------

type Script = (request: ProviderRequest) => Promise<Message> | Message;

/** Turn a finished message into the stream events a real run would have produced. */
export async function* replayMessage(message: Message, pace = 0): AsyncGenerator<StreamEvent> {
  const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
  yield { type: 'message_start', message: { ...message, content: [], stop_reason: null, stop_sequence: null } } as StreamEvent;
  for (const [index, block] of message.content.entries()) {
    if (block.type === 'text') {
      yield { type: 'content_block_start', index, content_block: { type: 'text', text: '', citations: null } } as StreamEvent;
      for (const chunk of block.text.match(/.{1,12}/gs) ?? []) {
        if (pace) await wait(pace);
        yield { type: 'content_block_delta', index, delta: { type: 'text_delta', text: chunk } } as StreamEvent;
      }
    } else if (block.type === 'tool_use') {
      yield { type: 'content_block_start', index, content_block: { type: 'tool_use', id: block.id, name: block.name, input: {} } } as StreamEvent;
      if (pace) await wait(pace);
      yield { type: 'content_block_delta', index, delta: { type: 'input_json_delta', partial_json: JSON.stringify(block.input) } } as StreamEvent;
    } else if (block.type === 'thinking') {
      yield { type: 'content_block_start', index, content_block: { type: 'thinking', thinking: '', signature: '' } } as StreamEvent;
      yield { type: 'content_block_delta', index, delta: { type: 'thinking_delta', thinking: block.thinking } } as StreamEvent;
    }
    yield { type: 'content_block_stop', index } as StreamEvent;
  }
  yield { type: 'message_delta', delta: { stop_reason: message.stop_reason, stop_sequence: null }, usage: message.usage } as unknown as StreamEvent;
  yield { type: 'message_stop' } as StreamEvent;
}

export function mockMessage(content: Message['content'], stopReason: Message['stop_reason'] = content.some(block => block.type === 'tool_use') ? 'tool_use' : 'end_turn'): Message {
  return {
    id: 'msg_mock_' + Math.random().toString(36).slice(2, 10), type: 'message', role: 'assistant', model: 'mock', content,
    stop_reason: stopReason, stop_sequence: null, stop_details: null,
    usage: { input_tokens: 0, output_tokens: 0, cache_creation_input_tokens: 0, cache_read_input_tokens: 0, cache_creation: null, server_tool_use: null, service_tier: null, inference_geo: null, iterations: null, speed: null },
  } as unknown as Message;
}

/** A provider that answers from a script; the default script exercises reading, an edit and a reply. */
export class MockProvider implements AgentProvider {
  readonly id = 'mock';
  constructor(private readonly script: Script = defaultScript, private readonly pace = 0) {}
  run(request: ProviderRequest): ProviderRun {
    const message = Promise.resolve().then(() => this.script(request));
    const pace = this.pace;
    const events = (async function* () {
      if (request.signal.aborted) throw Object.assign(new Error(t('#AgentStopped')), { name: 'AbortError' });
      yield* replayMessage(await message, pace);
    })();
    return { events, final: () => message };
  }
}

const lastUserText = (request: ProviderRequest): string => {
  for (let i = request.messages.length - 1; i >= 0; i--) {
    const message = request.messages[i];
    if (message.role !== 'user') continue;
    const blocks = typeof message.content === 'string' ? [{ type: 'text', text: message.content }] : message.content;
    const text = blocks.filter((block): block is { type: 'text'; text: string } => (block as { type: string }).type === 'text').map(block => block.text).join('\n');
    if (text) return text;
  }
  return '';
};
const lastMessageHasToolResults = (request: ProviderRequest): boolean => {
  const last = request.messages[request.messages.length - 1];
  return !!last && last.role === 'user' && typeof last.content !== 'string' && last.content.some(block => block.type === 'tool_result');
};

// i18n-ignore-start: words matched in what a person typed
const QUESTION_WORDS: ReadonlySet<string> = new Set(['что', 'как', 'какой', 'какая', 'какое', 'какие', 'каком', 'сколько', 'где', 'куда', 'почему', 'зачем', 'кто', 'когда', 'чем', 'чей',
  'what', 'which', 'how', 'where', 'why', 'who', 'when', 'whose']);
// i18n-ignore-end

/**
 * The AI Agent hands each keyword to its subagent in one delegate call (or reads the scene for a question); a subagent
 * reads the scene, acts on its keyword with the tools it has, then reports — enough to see every chat state.
 */
export const defaultScript: Script = (request) => {
  const text = lastUserText(request).toLowerCase();
  // Words count only in what the person typed or the task says: the project map before it names rooms and items too.
  const typed = text.includes('user: ') ? text.slice(text.lastIndexOf('user: ') + 6) : text.includes('task from ') ? text.slice(text.lastIndexOf('task from ')) : text;
  // i18n-ignore-start: words matched in what a person typed
  const asks = (russian: string, english: RegExp) => typed.includes(russian) || english.test(typed);
  if (asks('ошибк', /\berror/)) { const error = new Error('Mock failure'); throw error; }
  // A question asks for nothing, whatever it names: only a request word hands the work on. JS `\b` sees no Cyrillic words.
  const question = /\?\s*$/.test(typed) || QUESTION_WORDS.has(typed.trim().split(/[\s,!.?]+/)[0]);
  const wants = !question && (/(сдела(й|ть)|добав(ь|ить)|постав(ь|ить)|покрас(ь|ить)|постро(й|ить)|нарисуй|нарисовать|расстав(ь|ить)|замени|убер(и|ите)|убрать)/.test(typed) || /\b(make|add|build|paint|put|place|replace|remove)\b/.test(typed));
  const room = wants && asks('комнат', /\broom/), table = wants && asks('стол', /\b(table|desk)/), paint = wants && (asks('цвет', /\b(colou?r|paint)/) || typed.includes('покрас'));
  // i18n-ignore-end
  const has = (name: string) => request.tools.some(tool => tool.name === name);
  const catalogId = text.match(/catalog:(\S+)/)?.[1] ?? 'missing-id';
  if (has('delegate')) {
    const tasks = [
      ...(room ? [{ agent_id: 'layout', task: 'Add a 4×3 m living room at 0,0.' }] : []),
      ...(table ? [{ agent_id: 'catalog', task: `Add the table catalog:${catalogId}` }] : []),
      ...(paint ? [{ agent_id: 'style', task: 'Paint the walls of room 0 #e8e1d5.' }] : []),
    ];
    if (!lastMessageHasToolResults(request) && tasks.length) return mockMessage([{ type: 'tool_use', id: 'toolu_mock_delegate', name: 'delegate', input: { tasks } }] as Message['content']);
  }
  if (!lastMessageHasToolResults(request)) {
    return mockMessage([
      { type: 'text', text: t('#AgentMockLooking'), citations: null },
      { type: 'tool_use', id: 'toolu_mock_scene', name: 'get_scene', input: {} },
    ] as Message['content']);
  }
  const previousToolNames = request.messages.flatMap(message => message.role === 'assistant' && typeof message.content !== 'string' ? message.content.filter(block => block.type === 'tool_use').map(block => (block as { name: string }).name) : []);
  if (room && has('add_room') && !previousToolNames.includes('add_room')) {
    return mockMessage([{ type: 'tool_use', id: 'toolu_mock_room', name: 'add_room', input: { x: 0, z: 0, width: 4, depth: 3, name: t('#AgentMockRoomName'), room_type: 'livingroom' } }] as Message['content']);
  }
  if (table && has('add_item') && !previousToolNames.includes('add_item')) {
    return mockMessage([{ type: 'tool_use', id: 'toolu_mock_item', name: 'add_item', input: { catalog_id: catalogId } }] as Message['content']);
  }
  if (paint && has('set_room_finish') && !previousToolNames.includes('set_room_finish')) {
    return mockMessage([{ type: 'tool_use', id: 'toolu_mock_paint', name: 'set_room_finish', input: { room_index: 0, wall_color: '#e8e1d5' } }] as Message['content']);
  }
  return mockMessage([{ type: 'text', text: t('#AgentMockDone'), citations: null }] as Message['content']);
};
